const FLEX_NUMBER_TOKEN = /[-+]?\d*\.?\d+(?:[eE][-+]?\d+)?/;

export function parseFlexibleNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (value == null) return null;

  const raw = String(value).trim();
  if (!raw) return null;

  const paren = raw.match(/^\((.*)\)$/);
  if (paren) {
    const inner = parseFlexibleNumber(paren[1]);
    return inner == null ? null : -Math.abs(inner);
  }

  const normalized = raw
    .replace(/[,_\s$€£¥%]/g, '')
    .replace(/\u00A0/g, '');

  const suffix = normalized.match(/^([-+]?\d*\.?\d+(?:[eE][-+]?\d+)?)([kKmMbB])$/);
  if (suffix) {
    const base = Number(suffix[1]);
    if (!Number.isFinite(base)) return null;
    const mult = suffix[2].toLowerCase() === 'k'
      ? 1_000
      : suffix[2].toLowerCase() === 'm'
        ? 1_000_000
        : 1_000_000_000;
    return base * mult;
  }

  const direct = Number(normalized);
  if (Number.isFinite(direct)) return direct;

  const token = raw.replace(/,/g, '').match(FLEX_NUMBER_TOKEN)?.[0];
  if (!token) return null;
  const parsed = Number(token);
  return Number.isFinite(parsed) ? parsed : null;
}

const hasExplicitLotText = (value: unknown): boolean => {
  if (typeof value !== 'string') return false;
  const s = value.toLowerCase();
  return /(micro|mini|full|standard)\s*lots?|\blots?\b/.test(s);
};

export function parseForexQuantityValue(value: unknown): number {
  const parsed = parseFlexibleNumber(value);
  if (parsed == null) return 0;
  if (typeof value !== 'string') return parsed;

  const s = value.toLowerCase();
  if (/\bmicro\b/.test(s)) return parsed * 0.01;
  if (/\bmini\b/.test(s)) return parsed * 0.1;
  return parsed;
}

export function parseTradeQuantityValue(value: unknown): number {
  const parsed = parseFlexibleNumber(value);
  return parsed ?? 0;
}

export function toDisplayForexLots(value: unknown): number | null {
  const parsed = parseForexQuantityValue(value);
  if (!Number.isFinite(parsed)) return null;

  const abs = Math.abs(parsed);
  if (hasExplicitLotText(value)) return abs;
  return abs >= 1000 ? abs / 100000 : abs;
}

export function formatQuantityDisplayValue(value: number | null | undefined, maxFractionDigits = 12): string {
  if (value == null || !Number.isFinite(value)) return '';
  return Number(value).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: Math.max(0, maxFractionDigits),
  });
}
